import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Tier,
  EducationalInvestment,
  ColorToken,
  ActionTabs,
  ActionTabItem,
  NUGGET_VARIANTS,
  blueprintTransform,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as BlueprintText } from '@backrs_ui/grow-ui/public/images/light-blueprint-text-transparent-background.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { usePageModal } from '@backrs_ui/grow-ui/src/hooks/usePageModal';
import { CareerPathWithScore, CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
  usePostBookmarkMutation,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { Box, Button, Snackbar, SxProps, Theme, useMediaQuery } from '@mui/material';

import { AspirationCardGrid, type BlueprintOwner } from './molecules/AspirationCardGrid';
import { BlueprintSection } from './molecules/BlueprintSection';
import { LockInSuccessAlert } from './molecules/ExploreTradeoffs/LockInSuccessAlert';
import { ShareBlueprintModal } from './molecules/ShareBlueprintModal';
import { StartOverConfirmationModal } from './molecules/StartOverConfirmationModal';

type BlueprintOutputPageProps = Readonly<{
  tier: Tier;
  careerPathSuggestions: CareerPathWithScore[];
  userResponseSet: CareerUserResponseSet;
  educationalInvestment?: { target: EducationalInvestment[]; others: EducationalInvestment[] };
  /** Set when the blueprint belongs to someone other than the signed-in user. */
  owner?: BlueprintOwner;
  onViewAllCareerPaths?: () => void;
  onCareerPathSelect?: (careerPath: CareerPathWithScore['careerPath']) => void;
  isShared?: boolean;
  isReadOnly?: boolean;
}>;

export function BlueprintOutputPage({
  tier,
  careerPathSuggestions,
  userResponseSet,
  educationalInvestment,
  owner,
  onViewAllCareerPaths,
  onCareerPathSelect,
  isShared = false,
  isReadOnly = false,
}: BlueprintOutputPageProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  // Bookmark state
  const { data: bookmarks, isLoading: bookmarksLoading } = useGetBookmarksQuery(undefined, {
    skip: isReadOnly,
  });
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const bookmarkId = getBookmarkIdForNugget(bookmarks, undefined, NUGGET_VARIANTS.BLUEPRINT);

  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [bookmarkError, setBookmarkError] = useState(false);
  const [isStartOverModalOpen, setIsStartOverModalOpen] = useState(false);
  const [showLockInSuccess, setShowLockInSuccess] = useState(false);

  const prevResponseSetRef = useRef(userResponseSet);

  useEffect(() => {
    const prev = prevResponseSetRef.current;

    if (prev !== userResponseSet) {
      if (prev && prev.id === userResponseSet.id) {
        setShowLockInSuccess(true);
      }

      prevResponseSetRef.current = userResponseSet;
    }
  }, [userResponseSet]);

  const handleBookmark = async () => {
    try {
      if (bookmarkId) {
        await deleteBookmark(bookmarkId).unwrap();
      } else {
        await postBookmark({ type: 'blueprint', itemId: userResponseSet.id }).unwrap();
      }
    } catch (error) {
      console.error('Bookmark toggle failed:', error);
      setBookmarkError(true);
    }
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleAddTask = () => {
    dispatch(openPlaylistListStep());
    dispatch(setNuggetToAdd(blueprintTransform(userResponseSet.id)));
  };

  const pageModal = usePageModal(isReadOnly ? undefined : APP_ROUTE.MY_BLUEPRINT);

  const actionButtons: ActionTabItem[] = [
    {
      id: 'blueprint-bookmark-button',
      icon: bookmarkId ? 'BookmarkCheck' : 'Bookmark',
      onClick: handleBookmark,
      disabled: bookmarksLoading,
      tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
    },
    {
      id: 'blueprint-share-button',
      icon: 'Share06',
      onClick: handleShare,
      tooltipHoverText: commonAppStrings.modules.actionTabs.share,
    },
    ...(!isMobile
      ? [
          {
            id: 'blueprint-add-task-button',
            icon: 'BookmarkAdd',
            onClick: handleAddTask,
            tooltipHoverText: commonAppStrings.modules.actionTabs.task,
          } as ActionTabItem,
        ]
      : []),
  ];

  return (
    <Box sx={containerSx}>
      {/* Blueprint mosaic banner */}
      <Box sx={mosaicContainerSx}>
        {/* Tile grid background */}
        <Box sx={tileGridSx}>
          {TILE_COLORS.map((color, i) => (
            <Box key={i} sx={tileSx(color as ColorToken)} />
          ))}
        </Box>
        {/* Blueprint text overlay */}
        <Box sx={blueprintBannerSx}>
          <BlueprintText />
        </Box>
        {/* Action buttons */}
        {!isReadOnly && (
          <Box sx={floatingActionsSx}>
            {pageModal.hasModal && (
              <Button
                id="blueprint-page-modal-button"
                hierarchy="Secondary color"
                size="md"
                onClick={pageModal.open}
                sx={tourButtonSx}
              >
                {appStrings.modules.results.tour}
              </Button>
            )}
            <ActionTabs
              actions={actionButtons}
              showCopySuccess={showCopySuccess}
              onCopySuccessChange={setShowCopySuccess}
              borderColor="Colors/Border/border-primary"
            />
          </Box>
        )}
      </Box>

      {!isReadOnly && (
        <>
          <ShareBlueprintModal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} />
          {pageModal.modalNode}

          <StartOverConfirmationModal
            open={isStartOverModalOpen}
            onClose={() => setIsStartOverModalOpen(false)}
            onConfirm={() => {
              setIsStartOverModalOpen(false);
              navigate(APP_ROUTE.SURVEY);
            }}
          />

          <Snackbar
            open={bookmarkError}
            onClose={() => setBookmarkError(false)}
            autoHideDuration={3000}
            message="Failed to update bookmark. Please try again."
          />

          <LockInSuccessAlert
            open={showLockInSuccess}
            onClose={() => setShowLockInSuccess(false)}
          />
        </>
      )}

      {/* Summary of Life Aspirations — 8 category cards */}
      <AspirationCardGrid
        responseSetId={userResponseSet.id}
        owner={owner}
        onStartOver={isReadOnly ? undefined : () => setIsStartOverModalOpen(true)}
      />

      {/* Your Blueprint — 4 cards with accordion detail */}
      <BlueprintSection
        tier={tier}
        careerPathSuggestions={careerPathSuggestions}
        educationalInvestment={educationalInvestment}
        userResponseSet={userResponseSet}
        owner={owner}
        onViewAllCareerPaths={onViewAllCareerPaths}
        onCareerPathSelect={onCareerPathSelect}
        isShared={isShared}
        isReadOnly={isReadOnly}
      />
    </Box>
  );
}

// Gradient-ish tile colors: darker blues on the left, lighter toward the right,
// with slight variation so it doesn't look perfectly uniform.
// Row 1 and Row 2 each go dark → light independently.
const ROW_1 = [
  'Brand/300',
  'Brand/200',
  'Brand/300',
  'Brand/200',
  'Brand/100',
  'Brand/200',
  'Brand/100',
  'Brand/50',
  'Brand/100',
  'Brand/50',
  'Brand/100',
  'Brand/50',
  'Brand/50',
  'Brand/100',
  'Brand/50',
];
const ROW_2 = [
  'Brand/200',
  'Brand/300',
  'Brand/200',
  'Brand/100',
  'Brand/200',
  'Brand/100',
  'Brand/50',
  'Brand/100',
  'Brand/50',
  'Brand/100',
  'Brand/50',
  'Brand/50',
  'Brand/100',
  'Brand/50',
  'Brand/50',
];

// Extend each row to 30 tiles (covers ~2560px wide screens), then concat.
// auto-fill flows left→right, top→bottom, so row 1 tiles fill first, row 2 second.
const extend = (pattern: string[], count: number) =>
  Array.from({ length: count }, (_, i) => pattern[i % pattern.length]);
const TILE_COLORS = [...extend(ROW_1, 30), ...extend(ROW_2, 30)];

const mosaicContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const tileGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
  gridTemplateRows: 'repeat(2, 90px)',
  gridAutoRows: 0,
  overflow: 'hidden',
  gap: '1px',
  height: 'calc(2 * 90px + 1px)',
  backgroundColor: backrs.colors['Colors/Base/Black'],
  '@container (max-width: 600px)': {
    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
    gridTemplateRows: 'repeat(2, 70px)',
    height: 'calc(2 * 70px + 1px)',
  },
});

const tileSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors[color],
  });

const floatingActionsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-xl'],
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  '& .MuiTabs-root': {
    minHeight: '40px',
  },
  '@media (max-width: 600px)': {
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
  },
});

const tourButtonSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 'fit-content',
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const blueprintBannerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  pointerEvents: 'none',
  marginLeft: backrs.spacing['spacing-4xl'],
  '& > svg': {
    backgroundImage: `linear-gradient(${backrs.colors['Brand/500']}33 1px, transparent 1px),
      linear-gradient(90deg, ${backrs.colors['Brand/500']}33 1px, transparent 1px)`,
    backgroundSize: '15px 15px',
    backgroundColor: backrs.colors['Colors/Base/White'],
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    padding: backrs.spacing['spacing-xl'],
    height: '70%',
    width: 'auto',
  },
  '@container (max-width: 1015px)': {
    marginLeft: backrs.spacing['spacing-2xl'],
    '& > svg': {
      height: '60%',
      padding: backrs.spacing['spacing-lg'],
    },
  },
  '@container (max-width: 600px)': {
    '& > svg': {
      height: '50%',
    },
  },
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-4xl'],
  flexDirection: 'column',
  width: '100%',
  '@container (max-width: 600px)': {
    gap: backrs.spacing['spacing-2xl'],
  },
  minHeight: '100%',
  containerType: 'inline-size',
  pb: backrs.spacing['spacing-6xl'],
});
